import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, MapPin, Loader2, Star, Calendar, Bed, Car, Plane, Utensils, Check, Ban, ListChecks, Clock, Wand2, X, ShieldCheck } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import api, { formatApiError, handleActError } from "@/lib/api";
import { toast } from "sonner";
import { useCompare, CompareBar, PkgCompare } from "@/components/PkgCompareBar";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const TABS = [
  ["overview", "Overview"],
  ["itinerary", "Itinerary"],
  ["stays", "Stays & transport"],
  ["inclusions", "Inclusions"],
  ["info", "Important info"],
];

export default function PackageDetailPage({ svc: svcProp }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const [svc, setSvc] = useState(svcProp || null);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("overview");
  const [variantVid, setVariantVid] = useState("");
  const [pax, setPax] = useState({ adults: 2, children: 0, infants: 0 });
  const [selAddons, setSelAddons] = useState([]);
  const [form, setForm] = useState({ customer_phone: "", notes: "" });
  const [booking, setBooking] = useState(false);
  const [showCustomise, setShowCustomise] = useState(false);
  const { compare, inCompare, toggle: toggleCompare, clear: clearCompare } = useCompare();
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    if (svcProp) { setSvc(svcProp); return; }
    api.get(`/marketplace/public/service/${slug}`).then((r) => setSvc(r.data.service)).catch(() => setSvc(false));
  }, [slug, svcProp]);
  useEffect(() => {
    if (svc?.service_id) {
      document.title = `${svc.name} — Suffle`;
      const av = (svc.variants || []).find((v) => v.available !== false);
      if (av) setVariantVid(av.vid);
      api.get(`/marketplace/public/service/${svc.service_id}/reviews`).then((r) => setReviews(r.data.reviews || [])).catch(() => {});
    }
  }, [svc?.service_id]); // eslint-disable-line

  const h = svc?.holiday || {};
  const brand = svc?.seller_brand || null;
  const accent = brand?.accent || "#c026d3";
  const pmode = svc?.pricing_mode || "fixed";
  const isQuoteMode = pmode === "quote" || pmode === "negotiable";
  const variants = (svc?.variants || []).filter((v) => v.available !== false);
  const selVar = variants.find((v) => v.vid === variantVid) || null;
  const pricing = h.pricing || {};
  const basis = pricing.basis || "per_package";
  const addOns = svc?.add_ons || [];

  const total = useMemo(() => {
    if (!svc || isQuoteMode) return null;
    const adultRate = selVar ? Number(selVar.price) : Number(pricing.adult || svc.price || 0);
    const base = basis === "per_person"
      ? (pax.adults * adultRate) + (pax.children * Number(pricing.child || 0)) + (pax.infants * Number(pricing.infant || 0))
      : adultRate;
    const addonSum = selAddons.reduce((s, l) => s + Number((addOns.find((a) => a.label === l) || {}).amount || 0), 0);
    return Math.round((base + addonSum) * 100) / 100;
  }, [svc, isQuoteMode, selVar, basis, pax, pricing, selAddons, addOns]);

  const baseRate = () => (selVar ? Number(selVar.price) : Number(pricing.adult || svc?.price || 0));
  const priceLabel = () => {
    if (pmode === "quote") return "On request";
    if (pmode === "negotiable") return "Negotiable";
    if (pmode === "starting_from") return `From ₹${baseRate()}`;
    return `₹${baseRate()}`;
  };
  const priceSub = () => {
    if (isQuoteMode) return "Enquire for a tailored price";
    return basis === "per_person" ? "per adult" : "total package price";
  };

  const toggleAddon = (label) => setSelAddons((s) => (s.includes(label) ? s.filter((x) => x !== label) : [...s, label]));

  const doBook = async () => {
    setBooking(true);
    try {
      const r = await api.post("/services/book", { service_id: svc.service_id, variant_vid: variantVid || null, adults: pax.adults, children: pax.children, infants: pax.infants, addons: selAddons, ...form });
      const pay = r.data.payment; const st = r.data.booking?.status;
      if (st === "quote_requested") { toast.success("Enquiry sent — the agent will send you a price."); nav("/app/bookings"); return; }
      if (pay?.required) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Couldn't load the payment window."); setBooking(false); return; }
        const rzp = new window.Razorpay({
          key: pay.key_id, order_id: pay.order_id, amount: pay.amount, currency: pay.currency,
          name: "Suffle Holidays", description: svc.name,
          handler: async (resp) => {
            try { await api.post("/services/book/verify-payment", { booking_id: r.data.booking.booking_id, ...resp }); toast.success("Payment confirmed — your trip is booked!"); nav(`/app/booking/${r.data.booking.booking_id}/confirmed`); }
            catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          },
        });
        rzp.on("payment.failed", (x) => toast.error("Payment failed", { description: x?.error?.description }));
        rzp.open(); setBooking(false);
      } else { toast.success("Booking confirmed!"); nav(`/app/booking/${r.data.booking.booking_id}/confirmed`); }
    } catch (e) {
      if (!handleActError(e, nav)) toast.error(formatApiError(e.response?.data?.detail));
      setBooking(false);
    }
  };

  if (svc === null) return <PublicShell><div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div></PublicShell>;
  if (svc === false) return <PublicShell><div className="p-16 text-center text-slate-500">Package not found.</div></PublicShell>;

  const cover = (svc.images || [])[0];
  const nights = h.nights, days = h.days;
  const durationChip = days || nights ? `${days || "?"}D / ${nights || "?"}N` : null;

  return (
    <PublicShell>
      <div data-testid="package-detail">
        {/* Hero */}
        <div className="relative overflow-hidden" style={{ background: "#0b1020" }}>
          {cover && <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />}
          <div className="relative mx-auto max-w-6xl px-4 py-12 text-white">
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: accent }}><Sparkles className="h-3.5 w-3.5" /> {h.package_type || "Holiday Package"}</div>
            <h1 className="mt-3 max-w-3xl text-4xl font-bold sm:text-5xl" data-testid="package-title">{svc.name}</h1>
            {h.short_desc && <p className="mt-2 max-w-2xl text-base text-white/80">{h.short_desc}</p>}
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              {durationChip && <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1"><Clock className="h-4 w-4" /> {durationChip}</span>}
              {(h.stops || []).length > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1"><MapPin className="h-4 w-4" /> {(h.stops || []).join(" · ")}</span>}
              {svc.rating_count ? <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1"><Star className="h-4 w-4 text-amber-300" /> {svc.rating_avg} ({svc.rating_count})</span> : null}
            </div>
            <p className="mt-3 flex items-center gap-2 text-sm text-white/70">by {svc.provider?.name || "Verified agent"}{svc.verified_seller && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-2 py-0.5 text-[11px] font-semibold text-white" data-testid="package-verified"><ShieldCheck className="h-3.5 w-3.5" /> Verified agent</span>}</p>
            <button onClick={() => toggleCompare(svc)} data-testid={`package-compare-${svc.service_id}`} className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${inCompare(svc.service_id) ? "bg-fuchsia-600 text-white" : "bg-white/90 text-slate-800 hover:bg-white"}`}>{inCompare(svc.service_id) ? "✓ Added to compare" : "+ Compare"}</button>
          </div>
        </div>

        <div className="mx-auto max-w-6xl gap-8 px-4 py-8 lg:grid lg:grid-cols-[1.6fr_1fr]">
          {/* Left: tabbed content */}
          <div>
            <div className="mb-5 flex flex-wrap gap-2 border-b border-slate-100 pb-2" data-testid="package-tabs">
              {TABS.map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`package-tab-${k}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${tab === k ? "text-white" : "text-slate-500 hover:bg-slate-50"}`} style={tab === k ? { background: accent } : undefined}>{l}</button>
              ))}
            </div>

            {tab === "overview" && (
              <div className="space-y-5" data-testid="package-overview">
                {svc.description && <p className="whitespace-pre-wrap text-slate-700">{svc.description}</p>}
                {(h.highlights || []).length > 0 && (
                  <div>
                    <h3 className="mb-2 text-lg font-bold text-slate-800">Highlights</h3>
                    <ul className="grid gap-2 sm:grid-cols-2">
                      {h.highlights.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Sparkles className="mt-0.5 h-4 w-4 shrink-0" style={{ color: accent }} /> {x}</li>)}
                    </ul>
                  </div>
                )}
                {(h.themes || []).length > 0 && <div className="flex flex-wrap gap-2">{h.themes.map((t) => <span key={t} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{t}</span>)}</div>}
                {h.media?.video_url && <a href={h.media.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: accent }} data-testid="package-video"><Plane className="h-4 w-4" /> Watch the trip video</a>}
              </div>
            )}

            {tab === "itinerary" && (
              <div data-testid="package-itinerary">
                {(h.itinerary || []).length === 0 ? <p className="text-sm text-slate-400">The day-by-day plan will be shared by the agent.</p> : (
                  <ol className="relative border-l-2 border-slate-100 pl-6">
                    {h.itinerary.map((d, i) => (
                      <li key={i} className="mb-6" data-testid={`package-day-${i}`}>
                        <span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ background: accent }}>{d.day || i + 1}</span>
                        <h4 className="font-bold text-slate-800">Day {d.day || i + 1}{d.title ? ` — ${d.title}` : ""}</h4>
                        {d.desc && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{d.desc}</p>}
                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                          {d.hotel && <span className="inline-flex items-center gap-1"><Bed className="h-3.5 w-3.5" /> {d.hotel}</span>}
                          {d.meals && <span className="inline-flex items-center gap-1"><Utensils className="h-3.5 w-3.5" /> {d.meals}</span>}
                        </div>
                        {(d.activities || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{d.activities.map((a, j) => <span key={j} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] text-slate-600">{a}</span>)}</div>}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}

            {tab === "stays" && (
              <div className="space-y-5" data-testid="package-stays">
                <div>
                  <h3 className="mb-2 text-lg font-bold text-slate-800"><Bed className="mr-1 inline h-5 w-5" /> Accommodation</h3>
                  {(h.accommodation?.hotels || []).length ? (
                    <div className="space-y-2">
                      {h.accommodation.hotels.map((ht, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 p-3 text-sm">
                          <span className="font-semibold text-slate-700">{ht.name}</span>
                          {ht.tier && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{ht.tier}</span>}
                          {ht.nights && <span className="text-slate-400">{ht.nights} night(s)</span>}
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-slate-400">{h.accommodation?.included === false ? "Accommodation not included." : "Comfortable stays included."}</p>}
                </div>
                <div>
                  <h3 className="mb-2 text-lg font-bold text-slate-800"><Car className="mr-1 inline h-5 w-5" /> Transport</h3>
                  {h.transport?.included === false ? <p className="text-sm text-slate-400">Transport not included.</p> : (
                    <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                      {h.transport?.type && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1">{h.transport.type}</span>}
                      {h.transport?.vehicle && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1"><Car className="h-4 w-4" /> {h.transport.vehicle}</span>}
                      {h.flight?.status && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-3 py-1 text-sky-700"><Plane className="h-4 w-4" /> Flights</span>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === "inclusions" && (
              <div className="grid gap-6 sm:grid-cols-2" data-testid="package-inclusions">
                <div>
                  <h3 className="mb-2 flex items-center gap-2 text-lg font-bold text-emerald-700"><ListChecks className="h-5 w-5" /> Inclusions</h3>
                  <ul className="space-y-1.5">{(h.inclusions || []).length ? h.inclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {x}</li>) : <li className="text-sm text-slate-400">Shared on request.</li>}</ul>
                </div>
                <div>
                  <h3 className="mb-2 flex items-center gap-2 text-lg font-bold text-rose-600"><Ban className="h-5 w-5" /> Exclusions</h3>
                  <ul className="space-y-1.5">{(h.exclusions || []).length ? h.exclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><X className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" /> {x}</li>) : <li className="text-sm text-slate-400">Shared on request.</li>}</ul>
                </div>
                {(h.activities || []).length > 0 && (
                  <div className="sm:col-span-2">
                    <h3 className="mb-2 text-lg font-bold text-slate-800">Optional experiences</h3>
                    <div className="flex flex-wrap gap-1.5">{h.activities.map((a, i) => <span key={i} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{a}</span>)}</div>
                  </div>
                )}
              </div>
            )}

            {tab === "info" && (
              <div className="space-y-4" data-testid="package-info">
                {h.terms?.cancellation && <div><h3 className="mb-1 font-bold text-slate-800">Cancellation policy</h3><p className="whitespace-pre-wrap text-sm text-slate-600">{h.terms.cancellation}</p></div>}
                {h.terms?.notes && <div><h3 className="mb-1 font-bold text-slate-800">Important information</h3><p className="whitespace-pre-wrap text-sm text-slate-600">{h.terms.notes}</p></div>}
                {(h.documents?.required || []).length > 0 && <div><h3 className="mb-1 font-bold text-slate-800">Documents required</h3><div className="flex flex-wrap gap-1.5">{h.documents.required.map((x) => <span key={x} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{x}</span>)}</div></div>}
                {h.documents?.visa_assist && <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><ShieldCheck className="h-4 w-4" /> Visa assistance provided</p>}
                {brand?.contact_line && <p className="text-sm text-slate-500">{brand.contact_line}</p>}
              </div>
            )}

            <SiaAsk serviceId={svc.service_id} accent={accent} />

            {reviews.length > 0 && (
              <div className="mt-8" data-testid="package-reviews">
                <h3 className="mb-2 text-lg font-bold text-slate-800">Traveller reviews</h3>
                {reviews.map((r) => (
                  <div key={r.review_id} className="mb-2 rounded-xl border border-slate-100 p-3 text-sm">
                    <p className="text-amber-500">{"★".repeat(r.rating)}<span className="text-slate-300">{"★".repeat(5 - r.rating)}</span></p>
                    <p className="text-slate-600">{r.comment}</p><p className="text-xs text-slate-400">{r.customer_name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: sticky booking panel */}
          <div className="mt-6 lg:mt-0">
            <div className="sticky top-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="package-book-panel" style={{ borderTop: `4px solid ${accent}` }}>
              {brand?.logo_url && <img src={brand.logo_url} alt="" className="mb-3 h-9 w-auto max-w-[140px] object-contain" />}
              <p className="text-2xl font-bold text-slate-900" data-testid="package-price">{priceLabel()}</p>
              <p className="mt-0.5 text-xs text-slate-500">{priceSub()}</p>

              {variants.length > 0 && (
                <div className="mt-4" data-testid="package-variants">
                  <label className="text-xs font-semibold text-slate-500">Choose a package option</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {variants.map((v) => (
                      <button key={v.vid} onClick={() => setVariantVid(v.vid)} data-testid={`package-variant-${v.vid}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${variantVid === v.vid ? "text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`} style={variantVid === v.vid ? { background: accent } : undefined}>{v.label} · ₹{v.price}</button>
                    ))}
                  </div>
                </div>
              )}

              {!isQuoteMode && (
                <div className="mt-4" data-testid="package-pax">
                  <label className="text-xs font-semibold text-slate-500">Travellers</label>
                  <div className="mt-1 space-y-1.5">
                    {[["adults", "Adults"], ["children", "Children"], ["infants", "Infants"]].map(([k, l]) => (
                      <div key={k} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-1.5">
                        <span className="text-sm text-slate-600">{l}</span>
                        <div className="flex items-center gap-3">
                          <button type="button" data-testid={`package-pax-${k}-dec`} onClick={() => setPax((p) => ({ ...p, [k]: Math.max(0, (p[k] || 0) - 1) }))} className="h-6 w-6 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50">−</button>
                          <span className="w-5 text-center text-sm font-semibold" data-testid={`package-pax-${k}-val`}>{pax[k] || 0}</span>
                          <button type="button" data-testid={`package-pax-${k}-inc`} onClick={() => setPax((p) => ({ ...p, [k]: (p[k] || 0) + 1 }))} className="h-6 w-6 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50">+</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {addOns.length > 0 && !isQuoteMode && (
                <div className="mt-4" data-testid="package-addons">
                  <label className="text-xs font-semibold text-slate-500">Add-ons</label>
                  <div className="mt-1 space-y-1">
                    {addOns.map((a) => (
                      <label key={a.label} className="flex cursor-pointer items-center justify-between rounded-md border border-slate-200 px-3 py-1.5 text-sm" data-testid={`package-addon-${a.label}`}>
                        <span className="inline-flex items-center gap-2 text-slate-600"><input type="checkbox" checked={selAddons.includes(a.label)} onChange={() => toggleAddon(a.label)} /> {a.label}</span>
                        <span className="font-semibold text-slate-700">+₹{a.amount}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {total != null && (
                <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2" data-testid="package-total">
                  <span className="text-sm font-semibold text-slate-500">Total</span>
                  <span className="text-lg font-bold text-slate-900">₹{total}</span>
                </div>
              )}

              {(h.pricing?.payment_plan || []).length > 0 && (
                <div className="mt-4 rounded-xl border border-slate-100 p-3" data-testid="package-payment-plan">
                  <p className="text-xs font-bold text-slate-700">Payment plan</p>
                  {h.pricing.payment_plan.map((r, i) => (
                    <div key={i} className="mt-1 flex items-center justify-between text-[11px] text-slate-600">
                      <span>{r.label}{r.due ? ` · ${r.due}` : ""}</span>
                      <span className="font-semibold">{r.pct}%{total != null ? ` · ₹${Math.round(total * Number(r.pct || 0) / 100)}` : ""}</span>
                    </div>
                  ))}
                  <p className="mt-1 text-[10px] text-slate-400">First instalment is charged now; the balance is paid to the agent per this schedule.</p>
                </div>
              )}

              <div className="mt-4 space-y-2">
                {!isQuoteMode && <label className="block text-[11px] font-semibold text-slate-500">Preferred travel date
                  <input type="date" value={form.travel_date || ""} onChange={(e) => setForm({ ...form, travel_date: e.target.value })} data-testid="package-travel-date" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
                </label>}
                <input placeholder="Phone number" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="package-phone" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
                <textarea placeholder="Anything the agent should know?" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="package-notes" className="h-14 w-full rounded-md border border-slate-200 p-2 text-sm" />
              </div>

              {isQuoteMode ? (
                <button onClick={() => setShowCustomise(true)} data-testid="package-enquire" className="mt-3 w-full rounded-full px-4 py-2.5 text-sm font-semibold text-white" style={{ background: accent }}>Enquire / Request a quote</button>
              ) : (
                <button onClick={doBook} disabled={booking} data-testid="package-book" className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50" style={{ background: accent }}>{booking ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Book this package</button>
              )}
              {h.customisation?.enabled !== false && (
                <button onClick={() => setShowCustomise(true)} data-testid="package-customise-open" className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"><Wand2 className="h-4 w-4" /> Customise this package</button>
              )}
              <p className="mt-2 text-center text-[11px] text-slate-400">Tell the agent your changes — they'll revise the plan and you finalise together.</p>
            </div>
          </div>
        </div>

        {showCustomise && <CustomiseModal svc={svc} accent={accent} variants={variants} defaultVariant={variantVid} defaultPax={pax} onClose={() => setShowCustomise(false)} onDone={() => { setShowCustomise(false); nav("/app/bookings"); }} />}
        <CompareBar compare={compare} onOpen={() => setShowCompare(true)} onClear={() => { clearCompare(); setShowCompare(false); }} />
        {showCompare && <PkgCompare items={compare} onClose={() => setShowCompare(false)} onRemove={toggleCompare} />}
      </div>
    </PublicShell>
  );
}

function CustomiseModal({ svc, accent, variants, defaultVariant, defaultPax, onClose, onDone }) {
  const nav = useNavigate();
  const [c, setC] = useState({ variant_vid: defaultVariant || "", preferred_dates: "", hotel_change: "", adults: defaultPax.adults || 2, children: defaultPax.children || 0, infants: defaultPax.infants || 0, customer_phone: "", notes: "" });
  const [addActs, setAddActs] = useState([]);
  const [rmActs, setRmActs] = useState([]);
  const [busy, setBusy] = useState(false);
  const optionalActs = svc.holiday?.activities || [];
  const itineraryActs = (svc.holiday?.itinerary || []).flatMap((d) => d.activities || []);
  const removable = [...new Set(itineraryActs)];
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const [addFree, setAddFree] = useState("");

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/services/package/${svc.service_id}/customise`, { ...c, add_activities: [...addActs, ...(addFree.trim() ? [addFree.trim()] : [])], remove_activities: rmActs });
      toast.success("Customisation request sent — the agent will revise and reply in your bookings.");
      onDone();
    } catch (e) {
      if (!handleActError(e, nav)) toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="customise-modal">
      <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="inline-flex items-center gap-2 text-lg font-bold text-slate-800"><Wand2 className="h-5 w-5" style={{ color: accent }} /> Customise: {svc.name}</h3>
          <button onClick={onClose} data-testid="customise-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {variants.length > 0 && (
          <label className="block text-xs font-semibold text-slate-500">Base option
            <select value={c.variant_vid} onChange={(e) => setC({ ...c, variant_vid: e.target.value })} data-testid="customise-variant" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm">
              <option value="">Any / not sure</option>
              {variants.map((v) => <option key={v.vid} value={v.vid}>{v.label} · ₹{v.price}</option>)}
            </select>
          </label>
        )}
        <label className="mt-3 block text-xs font-semibold text-slate-500">Preferred travel dates
          <input value={c.preferred_dates} onChange={(e) => setC({ ...c, preferred_dates: e.target.value })} placeholder="e.g. 12–18 Dec, or flexible" data-testid="customise-dates" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
        </label>
        <label className="mt-3 block text-xs font-semibold text-slate-500">Hotel change request
          <input value={c.hotel_change} onChange={(e) => setC({ ...c, hotel_change: e.target.value })} placeholder="e.g. Upgrade to a 5★ beach resort" data-testid="customise-hotel" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
        </label>
        {optionalActs.length > 0 && (
          <div className="mt-3" data-testid="customise-add-acts">
            <p className="text-xs font-semibold text-slate-500">Add activities</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {optionalActs.map((a) => <button key={a} type="button" onClick={() => toggle(addActs, setAddActs, a)} className={`rounded-full px-3 py-1 text-[11px] font-semibold ${addActs.includes(a) ? "text-white" : "border border-slate-200 text-slate-600"}`} style={addActs.includes(a) ? { background: accent } : undefined}>+ {a}</button>)}
            </div>
          </div>
        )}
        <label className="mt-2 block text-xs font-semibold text-slate-500">Add something not listed
          <input value={addFree} onChange={(e) => setAddFree(e.target.value)} placeholder="e.g. Candlelight dinner" data-testid="customise-add-free" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
        </label>
        {removable.length > 0 && (
          <div className="mt-3" data-testid="customise-rm-acts">
            <p className="text-xs font-semibold text-slate-500">Remove activities</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {removable.map((a) => <button key={a} type="button" onClick={() => toggle(rmActs, setRmActs, a)} className={`rounded-full px-3 py-1 text-[11px] font-semibold ${rmActs.includes(a) ? "bg-rose-500 text-white" : "border border-slate-200 text-slate-600"}`}>− {a}</button>)}
            </div>
          </div>
        )}
        <div className="mt-3 grid grid-cols-3 gap-2">
          {[["adults", "Adults"], ["children", "Children"], ["infants", "Infants"]].map(([k, l]) => (
            <label key={k} className="text-[11px] font-semibold text-slate-500">{l}
              <input type="number" min="0" value={c[k]} onChange={(e) => setC({ ...c, [k]: Number(e.target.value) || 0 })} data-testid={`customise-${k}`} className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
            </label>
          ))}
        </div>
        <input value={c.customer_phone} onChange={(e) => setC({ ...c, customer_phone: e.target.value })} placeholder="Phone number" data-testid="customise-phone" className="mt-3 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
        <textarea value={c.notes} onChange={(e) => setC({ ...c, notes: e.target.value })} placeholder="Any other requests…" data-testid="customise-notes" className="mt-2 h-16 w-full rounded-md border border-slate-200 p-2 text-sm" />
        <button onClick={submit} disabled={busy} data-testid="customise-submit" className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50" style={{ background: accent }}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Send customisation request</button>
        <p className="mt-2 text-center text-[11px] text-slate-400">The agent will revise the package and reply with a price — you can discuss & finalise in My Bookings.</p>
      </div>
    </div>
  );
}


function SiaAsk({ serviceId, accent }) {
  const [q, setQ] = useState("");
  const [a, setA] = useState("");
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true); setA("");
    try { const r = await api.post(`/services/package/${serviceId}/ask-sia`, { question: q }); setA(r.data.answer); }
    catch (e) { setA(formatApiError(e.response?.data?.detail) || "Sia is busy right now — please try again shortly."); }
    finally { setBusy(false); }
  };
  const chips = ["What's included?", "Is this good for families?", "What's the cancellation policy?"];
  return (
    <div className="mt-8 rounded-2xl border border-violet-200 bg-violet-50/60 p-4" data-testid="package-sia">
      <p className="flex items-center gap-2 text-sm font-bold text-slate-800"><Sparkles className="h-4 w-4 text-violet-600" /> Ask Sia about this package</p>
      <p className="mt-0.5 text-[11px] text-slate-500">Instant answers from this package's real details — hotels, itinerary, inclusions & more.</p>
      <div className="mt-2 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(); }} placeholder="e.g. Are flights included?" data-testid="package-sia-input" className="h-9 flex-1 rounded-full border border-slate-200 px-3 text-sm outline-none focus:border-violet-400" />
        <button onClick={ask} disabled={busy} data-testid="package-sia-ask" className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: accent }}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Ask</button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {chips.map((c) => <button key={c} onClick={() => { setQ(c); }} data-testid={`package-sia-chip-${c}`} className="rounded-full border border-violet-200 bg-white px-2.5 py-1 text-[11px] font-medium text-violet-700 hover:bg-violet-50">{c}</button>)}
      </div>
      {a && <div className="mt-3 rounded-xl bg-white p-3 text-sm text-slate-700" data-testid="package-sia-answer">{a}</div>}
    </div>
  );
}
